/**
 * MatterSurfaceContext — the matter workspace's binding of the shared
 * detail-surface context.
 *
 * The machinery (an optional context, a typed API, the selection shape) is in
 * `shared/surface/DetailSurfaceContext`. What is particular to matters is the
 * two types below: which pane ids exist, which kinds of row can be selected,
 * and what the reader pane can be asked to show.
 *
 * IT IS OPTIONAL ON PURPOSE. `useMatterSurface()` returns null outside the
 * provider, so a tab component rendered anywhere else keeps working: the call
 * sites fall back to the global side panel they used before.
 */

import {
  DetailSelection,
  DetailSurfaceApi,
  createDetailSurfaceContext,
} from '../../shared/surface/DetailSurfaceContext';
import { MatterPanelId } from './layout';

/** What the reader pane is currently showing. */
export type MatterReaderContent =
  | {
      kind: 'source';
      /** Header line for the pane. */
      title: string;
      subtitle?: string;
      /** Still streaming — the pane shows a loader over the metadata. */
      loading?: boolean;
      citation: any;
      matterId: string;
      sourceType: 'file' | 'law' | 'case' | 'document';
      userAccessLevel?: 'edit' | 'admin' | 'view';
      onDelete?: () => void;
    }
  | {
      kind: 'file';
      title: string;
      subtitle?: string;
      loading?: boolean;
      fileUrl?: string;
      content?: string;
      isPdf?: boolean;
    };

/**
 * The kinds of row the shared DETAIL container can edit.
 *
 * One selection at a time across the whole workspace: picking a task while a
 * question is open swaps the container's content rather than stacking.
 */
export type MatterItemKind = 'qa' | 'task' | 'suggestion' | 'note';

export type MatterSelection = DetailSelection<MatterItemKind>;

export type MatterSurfaceApi = DetailSurfaceApi<
  MatterPanelId,
  MatterItemKind,
  MatterReaderContent
>;

const binding = createDetailSurfaceContext<MatterSurfaceApi>();

export const MatterSurfaceProvider = binding.Provider;

/** Null outside a matter surface — callers fall back to their old behaviour. */
export const useMatterSurface = binding.useSurface;

export default binding.Context;
